/**
 * Prompt 4 + 10: the single chronological stream behind the FEED tab.
 *
 * The design asks for "one chronological stream of EVERYTHING" -- picks, sabotage plays,
 * autopick switches, commissioner actions and chat. This module is the merge, kept as pure
 * functions so the ordering rules can be tested without a socket, a database, or a render.
 *
 * ── ONE SOURCE, NOT PARALLEL COPIES ─────────────────────────────────────────
 *
 * The README is explicit that the board, ticker, rosters and player list all derive from
 * picks[]. So this builds feed rows FROM those same arrays rather than accumulating its own
 * event log. A separate log would be a second copy of the draft that can disagree with the
 * board, and the one thing worse than a missing feed row is a feed that contradicts the
 * board next to it.
 *
 * ── WHAT IS ACTUALLY AVAILABLE LIVE ─────────────────────────────────────────
 *
 * Only `nfl_chat_messages` is in the supabase_realtime publication (SAB-846 covers the rest
 * and is not done). So chat arrives live and everything else arrives on the existing poll
 * that already refreshes picks. `sources` below records which inputs were live, so the UI
 * can say so rather than implying the feed is realtime when most of it is not.
 */

export type FeedEventKind =
  | 'pick'
  | 'sabotage'
  | 'autopick_switch'
  | 'commissioner'
  | 'chat';

export interface FeedEvent {
  /** Stable across rebuilds of the same underlying row -- used as the React key. */
  id: string;
  kind: FeedEventKind;
  /** Epoch ms. Rows without a usable timestamp are dropped; see buildFeedEvents. */
  at: number;
  /** Title line. */
  title: string;
  /** Meta line: actor and/or context. */
  meta?: string;
  /** Pick rows only. */
  pickNumber?: number;
  round?: number;
  position?: string;
  playerName?: string;
  teamName?: string;
  /** True when this pick was made by the server rather than the manager. */
  auto?: boolean;
  /** True when this is the viewing user's own pick -- drives the orange treatment. */
  isMine?: boolean;
  /** Chat rows only. */
  authorName?: string;
  body?: string;
}

export interface FeedPickInput {
  pick_number: number;
  round?: number | null;
  team_id?: string | null;
  player_id?: string | null;
  player_name?: string | null;
  position?: string | null;
  picked_at?: string | null;
  auto_picked?: boolean | null;
}

export interface FeedSabotageInput {
  id: string;
  card_name?: string | null;
  effect_type?: string | null;
  created_at?: string | null;
  actor_team_name?: string | null;
  target_team_name?: string | null;
}

export interface FeedChatInput {
  id: string;
  message?: string | null;
  created_at?: string | null;
  authorName?: string | null;
  isMine?: boolean;
}

export interface FeedCommissionerInput {
  id: string;
  action: 'pause' | 'resume' | string;
  created_at?: string | null;
  actorName?: string | null;
}

export interface BuildFeedArgs {
  picks?: FeedPickInput[];
  sabotages?: FeedSabotageInput[];
  chat?: FeedChatInput[];
  commissioner?: FeedCommissionerInput[];
  /** team_id -> display name, so pick rows can name a team without a second fetch. */
  teamNames?: Record<string, string>;
  /** The viewing user's team, for the "own pick" treatment. */
  myTeamId?: string | null;
}

/** Epoch ms, or null when the value cannot be trusted. */
function ts(value: string | null | undefined): number | null {
  if (!value) return null;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Merge every input into one newest-first stream.
 *
 * Rows with an unparseable timestamp are DROPPED rather than coerced to 0 or to now().
 * Coercing to 0 buries them at the bottom forever; coercing to now() floats an old row to
 * the top of a live draft and claims something just happened. Dropping is the only option
 * that does not assert a time we do not have.
 */
export function buildFeedEvents(args: BuildFeedArgs): FeedEvent[] {
  const { picks = [], sabotages = [], chat = [], commissioner = [], teamNames = {}, myTeamId = null } = args;
  const out: FeedEvent[] = [];

  for (const p of picks) {
    if (!p.player_id) continue; // unfilled slot, not an event
    const at = ts(p.picked_at);
    if (at === null) continue;
    const teamName = (p.team_id && teamNames[p.team_id]) || '';
    out.push({
      id: `pick:${p.pick_number}`,
      kind: 'pick',
      at,
      title: p.player_name ?? 'Unknown player',
      meta: teamName,
      pickNumber: p.pick_number,
      round: p.round ?? undefined,
      position: p.position ?? undefined,
      playerName: p.player_name ?? undefined,
      teamName,
      auto: p.auto_picked === true,
      isMine: !!myTeamId && p.team_id === myTeamId,
    });
  }

  for (const s of sabotages) {
    const at = ts(s.created_at);
    if (at === null) continue;
    const name = s.card_name ?? 'Sabotage';
    const target = s.target_team_name ? ` → ${s.target_team_name}` : '';
    out.push({
      id: `sab:${s.id}`,
      kind: 'sabotage',
      at,
      title: `${name}${target}`,
      meta: s.actor_team_name ?? undefined,
    });
  }

  for (const c of commissioner) {
    const at = ts(c.created_at);
    if (at === null) continue;
    out.push({
      id: `comm:${c.id}`,
      kind: 'commissioner',
      at,
      title: c.action === 'pause' ? 'Draft paused' : c.action === 'resume' ? 'Draft resumed' : `Commissioner: ${c.action}`,
      meta: c.actorName ?? 'Commissioner',
    });
  }

  for (const m of chat) {
    const at = ts(m.created_at);
    if (at === null) continue;
    out.push({
      id: `chat:${m.id}`,
      kind: 'chat',
      at,
      title: m.message ?? '',
      authorName: m.authorName ?? '',
      body: m.message ?? '',
      isMine: m.isMine === true,
    });
  }

  // Newest first. Ties break on id so the order is deterministic -- two picks can share a
  // timestamp at second resolution, and an unstable sort there makes rows swap on every
  // re-render for no reason the user can see.
  out.sort((a, b) => (b.at - a.at) || a.id.localeCompare(b.id));
  return out;
}

/** Filter chips: ALL / PICKS / sabotage. */
export type FeedFilter = 'all' | 'picks' | 'sabotage';

export function filterFeed(events: FeedEvent[], filter: FeedFilter): FeedEvent[] {
  if (filter === 'all') return events;
  if (filter === 'picks') return events.filter(e => e.kind === 'pick');
  return events.filter(e => e.kind === 'sabotage');
}

/*
 * SAB-992 removed `autopickTeamIds(picks)`.
 *
 * It answered "has this team ever been auto-picked for?" and drove the ticker's per-team
 * AUTO chip, where the question is actually "is this team on autodraft now?". Those differ
 * the moment a manager toggles the flag, which is the bug. The chip now reads
 * nfl_teams.auto_draft_enabled directly (NFLDraftRoomV2), so this had no remaining caller.
 * Deleted rather than left behind: a tested helper that nothing calls reads as live code.
 *
 * The per-PICK historical marker is unaffected -- that is `auto` on a feed event, built in
 * buildFeedEvents above and rendered by RightRail.
 */
